import axios from 'axios';
import type { AxiosError, Method } from 'axios';
import * as Rx from 'rxjs';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { API_MYEDUZZ } from 'settings';
import formatterApiErrors from 'v2/formatters/apiErrors';
import { apiRequestFormatter } from 'v2/formatters/apiRequest';

import eduzzAccountsService from './eduzzAccount';
import tokenService from './token';
import type { TokenService } from './token';

export class ApiService {
  private tokenRefreshRequest$: Rx.Observable<any>;

  constructor(private tokenService: TokenService) {}

  public get<T = any>(url: string, params?: any, headers?: any, oldAgent = false): Rx.Observable<T> {
    return this.request<T>('GET', url, params, headers, oldAgent).pipe(
      map(({ response }) => response),
      filter(response => response !== undefined)
    );
  }

  public post<T = any>(url: string, body: any, headers?: any, oldAgent = false, useRawError = false): Rx.Observable<T> {
    return this.request<T>('POST', url, body, headers, oldAgent, useRawError).pipe(
      map(({ response }) => response),
      filter(response => response !== undefined)
    );
  }

  public put<T = any>(url: string, body: any, headers?: any, oldAgent = false, useRawError = false): Rx.Observable<T> {
    return this.request<T>('PUT', url, body, headers, oldAgent, useRawError).pipe(
      map(({ response }) => response),
      filter(response => response !== undefined)
    );
  }

  public delete<T = any>(url: string, params?: any, headers?: any, oldAgent = false): Rx.Observable<T> {
    return this.request<T>('DELETE', url, params, headers, oldAgent).pipe(
      map(({ response }) => response),
      filter(response => response !== undefined)
    );
  }

  public upload<T = any>(url: string, data: FormData) {
    return this.request<T>('POST', url, data);
  }

  public download<T = any>(
    method: string,
    url: string,
    body: any,
    headers?: any,
    oldAgent = false,
    useRawError = false,
    responseType: 'json' | 'blob' = 'json'
  ): Rx.Observable<T> {
    return this.request<T>(method, url, body, headers, oldAgent, useRawError, responseType).pipe(
      map(({ response }) => response),
      filter(response => response !== undefined)
    );
  }

  private request<T = any>(
    method: Method | string,
    url: string,
    data: any = null,
    headers?: any,
    oldAgent: boolean = false,
    useRawError = false,
    responseType?: 'json' | 'blob'
  ): Rx.Observable<{ response: T; progress: number }> {
    const progress$ = new Rx.BehaviorSubject(0);

    return this.tokenService.getToken().pipe(
      first(),
      map(token => {
        //TODO REFAZER QUANDO ALTERAR PARA TOKEN SERVICE
        //if (!token) return {};
        if (!token) return null;
        return { ...(headers || {}), token };
      }),
      switchMap(headers => {
        return axios.request({
          baseURL: API_MYEDUZZ,
          url,
          method,
          headers: {
            ...headers,
            'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json'
          },
          responseType,
          params: method.toUpperCase() === 'GET' ? apiRequestFormatter(data) : null,
          data: method.toUpperCase() === 'POST' || method.toUpperCase() === 'PUT' ? apiRequestFormatter(data) : null,
          onUploadProgress: (progress: ProgressEvent) => {
            const result = progress.loaded / progress.total;
            progress$.next(result * 100);
          }
        });
      }),
      tap(() => {
        progress$.next(100);
        progress$.complete();
      }),
      //TODO ANALISAR POIS RETORNO DA API DO ORBITA EH DIFERENTE
      map(res => (oldAgent ? res : res?.data || null)),
      // map(res => apiResponseFormatter<T>(res.data && res.data.data ? res.data.data : res.data) || null),
      startWith(undefined),
      combineLatest(progress$.pipe(distinctUntilChanged()), (response, progress) => ({ response, progress })),
      catchError(err => {
        progress$.complete();
        return this.handleError(err, useRawError);
      })
    );
  }

  private handleError(err: AxiosError, useRawError = false) {
    if (useRawError) {
      return Rx.throwError(err);
    }

    if (!err.config || !err.response || !err.response?.status || err.response.status !== 401) {
      return Rx.throwError(formatterApiErrors(err));
    }

    if (err.response?.status === 401 && !err.config.headers.token) {
      return Rx.throwError(formatterApiErrors(err));
    }

    if (!this.tokenRefreshRequest$) {
      this.tokenRefreshRequest$ = this.tokenService.tryRefreshToken().pipe(
        switchMap(success => {
          this.tokenRefreshRequest$ = null;

          if (success) {
            return Rx.of(true);
          }

          eduzzAccountsService.logout();
          return Rx.never();
        }),
        shareReplay(1)
      );
    }

    const data = err.config.data && typeof err.config.data === 'string' ? JSON.parse(err.config.data) : err.config.data;
    const params =
      err.config.params && typeof err.config.params === 'string' ? JSON.parse(err.config.params) : err.config.params;

    return this.tokenRefreshRequest$.pipe(
      take(1),
      switchMap(() => this.request(err.config.method, err.config.url, data || params, err.config.headers))
    );
  }
}

const apiService = new ApiService(tokenService);
export default apiService;
